/**
 * Visual constants shared across the builder: per-primitive icons, colors,
 * and issue swatches. Centralized so the Library, node card, inspector, and
 * picker all show the same alphabet.
 */

import type * as React from 'react';
import {
  ArrowPathRoundedSquareIcon,
  BoltIcon,
  CheckBadgeIcon,
  ChevronUpDownIcon,
  DocumentArrowUpIcon,
  DocumentChartBarIcon,
  DocumentDuplicateIcon,
  DocumentMagnifyingGlassIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  LinkIcon,
  ListBulletIcon,
  PuzzlePieceIcon,
  ServerStackIcon,
  SparklesIcon,
  Squares2X2Icon,
  TableCellsIcon
} from '@heroicons/react/24/outline';

import type { InputSubtype, TaskDefinitionKey } from './types';

export const PRIMITIVE_ICON: Record<
  TaskDefinitionKey,
  React.ComponentType<React.ComponentProps<'svg'>>
> = {
  INPUT_DOCUMENT: DocumentTextIcon,
  INPUT_SPREADSHEET: TableCellsIcon,
  INPUT_TEXT: ListBulletIcon,
  INPUT_JSON: Squares2X2Icon,
  INPUT_AMS_READ: ServerStackIcon,
  INPUT_WEB_LOOKUP: GlobeAltIcon,
  EXTRACT: DocumentMagnifyingGlassIcon,
  AGGREGATE: ArrowPathRoundedSquareIcon,
  COMPARE: DocumentDuplicateIcon,
  VERIFY: CheckBadgeIcon,
  OUTPUT_DOCUMENT: DocumentChartBarIcon,
  OUTPUT_INTEGRATION: PuzzlePieceIcon
};

export const INPUT_SUBTYPE_ICON: Record<
  InputSubtype,
  React.ComponentType<React.ComponentProps<'svg'>>
> = {
  document: DocumentTextIcon,
  spreadsheet: TableCellsIcon,
  text: ListBulletIcon,
  json: Squares2X2Icon,
  ams: ServerStackIcon,
  web: GlobeAltIcon
};

export const CATEGORY_ICON = {
  input: DocumentArrowUpIcon,
  analysis: SparklesIcon,
  output: BoltIcon
};

export const LIBRARY_ICON_MISC = {
  binding: LinkIcon,
  pick: ChevronUpDownIcon
};

export const CATEGORY_ACCENT: Record<'input' | 'analysis' | 'output', string> = {
  input:
    'bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-300 ring-primary-600/20',
  analysis:
    'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 ring-indigo-600/20',
  output:
    'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-600/20'
};

export const CATEGORY_RING: Record<'input' | 'analysis' | 'output', string> = {
  input: 'ring-primary-200 dark:ring-primary-500/30',
  analysis: 'ring-indigo-200 dark:ring-indigo-500/30',
  output: 'ring-emerald-200 dark:ring-emerald-500/30'
};

export const CATEGORY_LABEL = {
  input: 'Inputs',
  analysis: 'Analysis',
  output: 'Outputs'
} as const;
